import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link2, Plus, Copy, Check, Trash2, ToggleLeft, ToggleRight, Users, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

function InviteCard({ invite, onDelete, onToggle, onCopy }) {
  const [copied, setCopied] = useState(false);
  const inviteUrl = `${FRONTEND_URL}/registro?invite=${invite.code}`;
  const isActive = invite.status === "active";
  const isFull = invite.used_count >= invite.max_uses;

  const handleCopy = () => {
    onCopy(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`bg-zinc-900/40 border rounded-2xl p-5 transition-all ${
        isActive && !isFull ? "border-zinc-800/50 hover:border-zinc-700" : "border-zinc-800/30 opacity-60"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium text-zinc-200 truncate">
              {invite.label || "Sin etiqueta"}
            </p>
            {isFull && (
              <span className="text-[9px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full font-medium">COMPLETO</span>
            )}
            {!isActive && (
              <span className="text-[9px] bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full font-medium">INACTIVO</span>
            )}
          </div>
          <p className="text-[11px] text-zinc-600 font-mono truncate">{inviteUrl}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-1.5">
          <Users size={12} className="text-zinc-500" />
          <span className="text-xs text-zinc-400">
            {invite.used_count} / {invite.max_uses} usos
          </span>
        </div>
        <span className="text-[10px] text-zinc-600">{invite.created_at?.slice(0, 10)}</span>
      </div>

      {/* Used by */}
      {invite.used_by && invite.used_by.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Registrados:</p>
          <div className="flex flex-wrap gap-1">
            {invite.used_by.map((email, i) => (
              <span key={i} className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full">
                {email}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          data-testid={`copy-invite-${invite.id}`}
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-medium transition-all"
        >
          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
          {copied ? "Copiado" : "Copiar link"}
        </button>
        <button
          data-testid={`toggle-invite-${invite.id}`}
          onClick={() => onToggle(invite.id)}
          className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-all"
          title={isActive ? "Desactivar" : "Activar"}
        >
          {isActive ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} />}
        </button>
        <button
          data-testid={`delete-invite-${invite.id}`}
          onClick={() => onDelete(invite.id)}
          className="p-1.5 rounded-lg hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-all"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </motion.div>
  );
}

export default function InvitationsView() {
  const [invites, setInvites] = useState([]);
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState(10);
  const [creating, setCreating] = useState(false);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/invitations`);
      setInvites(res.data);
    } catch (e) {
      console.error("Error fetching invites", e);
    }
  }, []);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/invitations`, {
        label: label.trim() || "Invitacion Agente 360",
        max_uses: maxUses,
      });
      setInvites((prev) => [res.data, ...prev]);
      setLabel("");
    } catch (e) {
      console.error("Error creating invite", e);
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/invitations/${id}`);
      setInvites((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      console.error("Error deleting invite", e);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.patch(`${API}/admin/invitations/${id}/toggle`);
      setInvites((prev) =>
        prev.map((i) => (i.id === id ? { ...i, status: res.data.status } : i))
      );
    } catch (e) {
      console.error("Error toggling invite", e);
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url).catch(() => {});
  };

  return (
    <div className="space-y-6">
      {/* Create new invite */}
      <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Link2 size={16} className="text-emerald-400" />
          <h3 className="text-sm font-semibold text-zinc-300 tracking-wide uppercase" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Crear Link de Invitacion
          </h3>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="invite-label-input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Etiqueta (ej: Grupo Enero 2026)"
            className="flex-1 bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-500 whitespace-nowrap">Max usos:</label>
            <input
              data-testid="invite-max-uses-input"
              type="number"
              min="1"
              max="1000"
              value={maxUses}
              onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
              className="w-20 bg-zinc-950/60 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 outline-none focus:border-zinc-600 transition-colors text-center"
            />
          </div>
          <button
            data-testid="create-invite-btn"
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-5 py-2.5 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.2)] whitespace-nowrap"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Crear Link
          </button>
        </div>
      </div>

      {/* Invites list */}
      {invites.length === 0 ? (
        <div className="text-center py-10">
          <Link2 size={28} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-xs text-zinc-600">No hay invitaciones creadas aun.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <AnimatePresence>
            {invites.map((invite) => (
              <InviteCard
                key={invite.id}
                invite={invite}
                onDelete={handleDelete}
                onToggle={handleToggle}
                onCopy={handleCopy}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
